import React, { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { X, EnvelopeSimple, Lock, ShieldCheck, GoogleLogo, CheckCircle } from "@phosphor-icons/react";
import { requestPasswordReset } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE REDIRECT URL — derive from window.location.origin.
function handleGoogleSignIn() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

/**
 * Inline auth modal triggered from anywhere in the guest experience
 * (Save button, sidebar CTA, Dashboard hero CTA, GuestGate views).
 *
 * Re-uses AuthContext.login / register so a successful sign-in promotes
 * the in-memory workspace state straight into the new account on the
 * next autosave tick — no data loss between guest and authed sessions.
 */
export default function AuthPromptModal({ open, onClose, defaultMode = "register" }) {
  const { login, register, authError, setAuthError } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  // Forgot-password sub-flow lives inside this modal to avoid yet another route.
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  if (!open) return null;

  async function submitForgot(e) {
    e.preventDefault();
    setAuthError("");
    if (!email.trim()) { setAuthError("Enter the email on your account."); return; }
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setForgotSent(true);
    } catch (err) {
      // Backend always returns 200; only true network errors land here.
      setAuthError(err?.response?.data?.detail || "Couldn't send right now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    setAuthError("");
    if (mode === "register") {
      if (password !== confirm) { setAuthError("Passwords do not match."); return; }
      if (password.length < 8) { setAuthError("Password must be at least 8 characters."); return; }
    }
    setBusy(true);
    try {
      if (mode === "register") {
        await register(email.trim(), password);
      } else {
        await login(email.trim(), password);
      }
      // Successful auth — close. App.js will detect user change and trigger
      // autosave of the in-memory snapshot to the new account.
      onClose?.();
    } catch (err) {
      console.debug("auth modal submit", err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" data-testid="auth-prompt-overlay">
      <div className="modal-panel auth-prompt-modal" onClick={(e) => e.stopPropagation()} data-testid="auth-prompt-modal">
        <div className="modal-header">
          <div className="auth-prompt-icon" aria-hidden="true">
            <ShieldCheck size={22} weight="duotone" />
          </div>
          <div>
            <div className="modal-title">Save your claim work</div>
            <div className="modal-subtitle">Your current data will be saved to your new account.</div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close" data-testid="auth-prompt-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="modal-body auth-prompt-body">
          {forgotMode ? (
            forgotSent ? (
              <div className="report-problem-success" data-testid="forgot-success">
                <CheckCircle size={42} weight="duotone" />
                <h3>Check your email</h3>
                <p>
                  If an account with <strong>{email}</strong> exists, we just sent a reset link.
                  It expires in 15 minutes — check spam if you don't see it.
                </p>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => { setForgotMode(false); setForgotSent(false); setMode("login"); }}
                  data-testid="forgot-back-to-login"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="auth-prompt-form" data-testid="forgot-form">
                <p className="auth-prompt-fineprint" style={{ marginTop: 0 }}>
                  Enter your account email and we'll send you a reset link.
                </p>
                <label className="field">
                  <span className="field-label"><EnvelopeSimple size={12} weight="bold" /> EMAIL</span>
                  <input
                    type="email"
                    className="field-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoFocus
                    data-testid="forgot-email"
                  />
                </label>
                {authError && <div className="auth-prompt-error" data-testid="forgot-error">{authError}</div>}
                <button
                  type="submit"
                  className="btn btn--accent auth-prompt-submit"
                  disabled={busy}
                  data-testid="forgot-submit"
                >
                  {busy ? "Sending..." : "Send reset link"}
                </button>
                <button
                  type="button"
                  className="link-btn auth-prompt-link"
                  onClick={() => { setForgotMode(false); setAuthError(""); }}
                  data-testid="forgot-cancel"
                >
                  Back to sign in
                </button>
              </form>
            )
          ) : (
          <>
          <div className="auth-prompt-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "register"}
              className={`auth-prompt-tab ${mode === "register" ? "auth-prompt-tab--active" : ""}`}
              onClick={() => { setMode("register"); setAuthError(""); }}
              data-testid="auth-prompt-tab-register"
            >
              Create account
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "login"}
              className={`auth-prompt-tab ${mode === "login" ? "auth-prompt-tab--active" : ""}`}
              onClick={() => { setMode("login"); setAuthError(""); }}
              data-testid="auth-prompt-tab-login"
            >
              Sign in
            </button>
          </div>

          <button
            type="button"
            className="btn btn--google auth-prompt-google"
            onClick={handleGoogleSignIn}
            data-testid="auth-prompt-google"
          >
            <GoogleLogo size={16} weight="bold" /> Continue with Google
          </button>
          <div className="auth-prompt-divider"><span>or</span></div>

          <form onSubmit={submit} className="auth-prompt-form">
            <label className="field">
              <span className="field-label"><EnvelopeSimple size={12} weight="bold" /> EMAIL</span>
              <input
                type="email"
                className="field-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
                data-testid="auth-prompt-email"
              />
            </label>
            <label className="field">
              <span className="field-label"><Lock size={12} weight="bold" /> PASSWORD</span>
              <input
                type="password"
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
                required
                data-testid="auth-prompt-password"
              />
            </label>
            {mode === "register" && (
              <label className="field">
                <span className="field-label"><Lock size={12} weight="bold" /> CONFIRM</span>
                <input
                  type="password"
                  className="field-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  data-testid="auth-prompt-confirm"
                />
              </label>
            )}

            {authError && (
              <div className="auth-prompt-error" data-testid="auth-prompt-error">{authError}</div>
            )}

            <button
              type="submit"
              className="btn btn--accent auth-prompt-submit"
              disabled={busy}
              data-testid="auth-prompt-submit"
            >
              {busy ? "Working..." : (mode === "register" ? "Create account & save" : "Sign in & save")}
            </button>
            {mode === "login" && (
              <button
                type="button"
                className="link-btn auth-prompt-link"
                onClick={() => { setForgotMode(true); setAuthError(""); }}
                data-testid="auth-prompt-forgot"
              >
                Forgot password?
              </button>
            )}
          </form>

          <div className="auth-prompt-fineprint">
            We never share your claim data. Free tier is generous — upgrade only when you want backups, more workspaces, and unlimited PDFs.
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
